import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo
} from "react";

// Third-party
import PropTypes from "prop-types";

// Services
import GoogleGeocoderService from "services/GoogleGeocoderService";

// Utils
import getenvvar from "utils/Env";

const GoogleMapsContext = createContext(null);

/**
 * Google Maps Provider
 * Manages a single instance of Google Maps API and related services
 */
export function GoogleMapsProvider({ children }) {
  const apiKey = getenvvar("REACT_APP_STREETVIEW_API_KEY");
  const [googleMaps, setGoogleMaps] = useState(null);
  const [geocoderService, setGeocoderService] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [geocoderRef, setGeocoderRef] = useState(null);

  // Load Google Maps API
  useEffect(() => {
    if (!apiKey) {
      setError(new Error("Google Maps API key is required"));
      setIsLoading(false);
      return;
    }

    // Check if Google Maps is already available
    if (window.google && window.google.maps) {
      setGoogleMaps(window.google.maps);
      return;
    }

    // Check if script already exists
    const existingScript = document.querySelector(
      `script[src^="https://maps.googleapis.com/maps/api/js"], script[data-google-maps-provider="true"]`
    );

    if (existingScript) {
      // Script exists but not loaded yet, wait for it
      const onScriptLoad = () => {
        if (window.google && window.google.maps) {
          setGoogleMaps(window.google.maps);
        }
      };

      const onScriptError = () => {
        setError(new Error("Google Maps script failed to load"));
        setIsLoading(false);
      };

      existingScript.addEventListener("load", onScriptLoad);
      existingScript.addEventListener("error", onScriptError);

      return () => {
        existingScript.removeEventListener("load", onScriptLoad);
        existingScript.removeEventListener("error", onScriptError);
      };
    }

    // No script exists, create one (but mark it to prevent duplicates)
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-maps-provider", "true"); // Mark our script

    // Define handlers
    script.onload = () => {
      if (window.google && window.google.maps) {
        setGoogleMaps(window.google.maps);
      } else {
        setError(new Error("Google Maps failed to load correctly"));
        setIsLoading(false);
      }
    };

    script.onerror = () => {
      setError(new Error("Error loading Google Maps script"));
      setIsLoading(false);
    };

    // Append to document head
    document.head.appendChild(script);

    // Don't remove script on cleanup to prevent issues with other components
    // The script should persist for the lifetime of the app
    return undefined;
  }, [apiKey]);

  // Create geocoder service when Google Maps API is loaded
  useEffect(() => {
    if (googleMaps) {
      try {
        const service = new GoogleGeocoderService(googleMaps);
        setGeocoderService(service);
        setIsLoading(false);
      } catch (err) {
        console.error("Failed to initialize GoogleGeocoderService:", err);
        setError(err);
        setIsLoading(false);
      }
    } else if (error) {
      console.error("Google Maps API error:", error);
      setIsLoading(false);
    }
  }, [googleMaps, error]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      googleMaps,
      geocoderService,
      isLoading,
      error,
      isReady: !isLoading && !error && googleMaps && geocoderService,
      geocoderRef,
      setGeocoderRef
    }),
    [googleMaps, geocoderService, isLoading, error, geocoderRef]
  );

  return (
    <GoogleMapsContext.Provider value={contextValue}>
      {children}
    </GoogleMapsContext.Provider>
  );
}

GoogleMapsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  apiKey: PropTypes.string
};

/**
 * Hook to use Google Maps API and services
 * @returns {Object} Google Maps context value with API, geocoder service, and state
 */
export function useGoogleMaps() {
  const context = useContext(GoogleMapsContext);

  if (!context) {
    throw new Error("useGoogleMaps must be used within a GoogleMapsProvider");
  }

  return context;
}

export default GoogleMapsProvider;
